/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2018 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import DtModObservable from '../Game/DtModObservable';
import IDependency from '../StateManager/IDependency';
import StateComponent from '../StateManager/StateComponent';
import StateEvent from '../StateManager/StateEvent';
import { StateID } from '../StateManager/StateID';
import StateManager from '../StateManager/StateManager';
import StateReference from '../StateManager/StateReference';
import InjectableComponent from '../Utility/Component/ComponentReference/InjectableComponent';
import ReferencedComponent from '../Utility/Component/ComponentReference/ReferencedComponent';
import Decorators from '../Utility/Decorators';

/** @hidden */
// tslint:disable-next-line:typedef
const property = cc._decorator.property;

/**
 * Base class for all effects.
 */
@cc._decorator.ccclass
@cc._decorator.disallowMultiple
export default abstract class Effect extends StateComponent implements IDependency
{
    /** Whether this effect is dependent on an outside state. */
    @property({ visible: true })
    protected _effectDependent: boolean = false;

    /** The external state this effect is depenedent upon. */
    @property({ type: StateReference, visible: (function(): boolean { return this._effectDependent; }) })
    protected primaryStateReference: StateReference = new StateReference();

    /** The ID to use for this effect's internal state. */
    @property({ override: true, visible: false })
    protected stateID: StateID = StateID.Custom;

    /** @hidden */
    @property({ visible: false, override: true })
    protected stateManager: StateManager = null;

    /** Used to determine whether or not the effect should trigger when its state is reached. */
    protected isStopping: boolean = false;

    /** Whether or not this effect should block its state. */
    @property({ visible: true })
    protected isBlocking: boolean = true;

    @property(InjectableComponent)
    protected reference: InjectableComponent<ReferencedComponent> = new InjectableComponent();

    /** @hidden */
    protected dtModRef: InjectableComponent<DtModObservable> = new InjectableComponent();

    /** @hidden */
    private _onCompleteFunctions: (() => any)[] = [];

    /** The implementation of the effect logic. */
    protected abstract trigger: () => void;
    /** Manually trigger the effect. */
    public Trigger = (): void => { this.trigger(); };

    /** @hidden */
    protected setContexts(): void
    {
        super.setContexts();

        this.reference.SetContext(ReferencedComponent);
        this.dtModRef.SetContext(DtModObservable);
    }

    /** @hidden */
    protected onLoad(): void
    {
        super.onLoad();

        this.NodeName = this.node.name;

        this.stateManager = this.getFirstParent(StateManager);

        let oldTrigger: () => void = this.trigger.bind(this);
        this.trigger = () =>
        {
            if (this.node.active) { oldTrigger(); }
            else { this.complete(); }
        };
    }

    /** @hidden */
    protected start(): void
    {
        this.bindListeners();
        super.start(false);
    }

    protected onDestroy(): void
    {
        this.unbindListeners();
        super.onDestroy();
    }

    /** @returns If this effect is still in its active state. */
    public IsCurrentState(): boolean
    {
        return !this.isComplete;
    }

    /** @hidden */
    @Decorators.BindThis()
    protected onEffectHandler = (event: StateEvent): void =>
    {
        this.checkTrigger();
    }

    /** @hidden */
    @Decorators.BindThis()
    protected onStateChangeHandler = (event: StateEvent): void =>
    {
        if (event.State === this.primaryStateReference.Value)
        {
            this.checkTrigger();
        }
    }

    /** Determines whether the effect should still play. */
    protected checkTrigger(): void
    {
        if (this.isStopping)
        {
            this.complete();
        }
        else
        {
            this.trigger();
        }
    }

    /** Sets internal state to reflect a completed effect. */
    protected complete(): void
    {
        this._onCompleteFunctions.forEach(fnOnComplete => fnOnComplete());

        this.isComplete = true;
        this.isStopping = false;
    }

    /** Add on complete function handler when effect complete. */
    public OnComplete(fnOnComplete: () => any): void
    {
        this._onCompleteFunctions.push(fnOnComplete);
    }

    /**
     * Stops the effect if it is currently playing or else marks the effect to not be played
     * on its next trigger.
     */
    public Stop(): void
    {
        if (this.isStopping) { return; }

        this.isStopping = true;
    }

    /** The name of the node this effect component is attached to. */
    public NodeName: string = '';

    /** @hidden */
    protected isComplete: boolean = false;
    /** @returns Whether the current effect has completed executing. */
    public IsComplete(state: StateComponent): boolean { return this.isComplete; }
    /** Resets the internal state so the effect is ready to fire again. */
    public Reset(): void
    {
        this.isComplete = false;
        this.isStopping = false;
    }

    private bindListeners(): void
    {
        this.OnStateChange(this.onEffectHandler);
        if (this.isBlocking) { this.AddDependency(this); }

        const primaryState: StateComponent = this.primaryStateReference ? this.primaryStateReference.Value : null;
        if (!this._effectDependent || !primaryState) { return; }

        primaryState.OnStateChange(this.onStateChangeHandler);
        if (this.isBlocking) { primaryState.AddDependency(this); }
    }

    private unbindListeners(): void
    {
        this.OffStateChange(this.onEffectHandler);
        if (this.isBlocking) { this.RemoveDependency(this); }

        const primaryState: StateComponent = this.primaryStateReference ? this.primaryStateReference.Value : null;
        if (!this._effectDependent || !primaryState) { return; }

        primaryState.OffStateChange(this.onStateChangeHandler);
        if (this.isBlocking) { primaryState.RemoveDependency(this); }
    }
}
